import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Check,
  X,
  Loader2,
  ChevronLeft,
  CheckCircle,
  XCircle,
  ArrowRight,
  Shield,
  Zap,
  Crown,
  Trophy,
  Users,
  GraduationCap,
  Clipboard,
  Search,
  Clock,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_ICONS = {
  basic: Shield,
  plus: Zap,
  elite: Crown,
  teams: Users,
  recruit: GraduationCap,
};

const CATEGORY_TABS = [
  { id: 'fans', label: 'Fans & Players', icon: Trophy, description: 'Individual & family plans' },
];

// Define the 3 main tiers with all pricing options
const FAN_TIERS = [
  {
    tier: 'basic',
    name: 'Rooam Basic',
    tagline: 'Watch. Follow. Stay Connected.',
    color: '#22c55e',
    pricing: {
      quarterly: { price: 8.99, label: '$8.99 billed quarterly' },
      yearly: { price: 35.96, label: '$35.96/year' },
    },
    asLowAs: '$3.00',
    features: [
      'Live game streaming',
      'Full replay access (no limits)',
      'Follow teams, players & events',
      'Personalized viewing feed',
      'Mobile, tablet & desktop access',
    ],
    notIncluded: [
      'No highlight clipping',
      'No athlete profile',
    ],
    perfectFor: 'Fans & families who just want to watch',
    highlight: false,
  },
  {
    tier: 'plus',
    name: 'Rooam Plus',
    tagline: 'Create. Clip. Build Your Profile.',
    color: '#3b82f6',
    pricing: {
      quarterly: { price: 29.99, label: '$29.99 billed quarterly' },
      yearly: { price: 119.96, label: '$119.96/year' },
    },
    asLowAs: '$10.00',
    features: [
      'Everything in Basic, plus:',
      'Content Tools:',
      'Highlight clipping tool',
      'Download & share clips',
      'Tag players in clips',
      'Athlete Features:',
      'Athlete profile (claim & manage)',
      'Highlight storage & organization',
      'Build your personal content library',
    ],
    perfectFor: 'Players and families who want highlights and visibility',
    highlight: true,
    badge: 'MOST POPULAR',
  },
  {
    tier: 'elite',
    name: 'Rooam Elite',
    tagline: 'Advanced Stats. Exposure. Recruiting.',
    color: '#ef4444',
    pricing: {
      quarterly: { price: 74.99, label: '$74.99 billed quarterly' },
      yearly: { price: 299.96, label: '$299.96/year' },
    },
    asLowAs: '$25.00',
    features: [
      'Everything in Plus, plus:',
      'AI + Performance:',
      'AI-generated highlights',
      'Advanced stats & analytics',
      'Game-by-game performance tracking',
      'Recruiting & Exposure:',
      'Access to recruiting database',
      'Increased visibility to college coaches',
      'Enhanced athlete profile (stats + analytics)',
      'Searchable recruiting profile',
      'Premium Experience:',
      'Priority processing for highlights',
      'Early access to new features',
    ],
    perfectFor: 'Serious athletes focused on recruiting & performance',
    highlight: false,
  },
];

const Membership = () => {
  const { token, user, isAuthenticated, isPendingPayment, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const isNewSignup = searchParams.get('signup') === 'true';
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(null);
  const [mySub, setMySub] = useState(null);
  const [activeTab, setActiveTab] = useState('fans');
  const [selectedInterval, setSelectedInterval] = useState({});
  const [renewalConsent, setRenewalConsent] = useState({});
  const [discountInput, setDiscountInput] = useState({});
  const [discountValid, setDiscountValid] = useState({});
  const [discountInfo, setDiscountInfo] = useState({});
  const [validatingCode, setValidatingCode] = useState(null);

  const sessionId = searchParams.get('session_id');
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [polling, setPolling] = useState(false);

  const fetchPlans = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/membership/plans`);
      if (res.ok) setPlans(await res.json());
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, []);

  const fetchMySub = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/membership/my-subscription`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setMySub(await res.json());
    } catch (err) {
      console.error(err);
    }
  }, [token]);

  useEffect(() => {
    fetchPlans();
    fetchMySub();
  }, [fetchPlans, fetchMySub]);

  useEffect(() => {
    if (!sessionId || paymentStatus) return;
    setPolling(true);
    let attempts = 0;
    const maxAttempts = 8;

    const poll = async () => {
      try {
        const res = await fetch(`${API_URL}/api/membership/status/${sessionId}`);
        if (res.ok) {
          const data = await res.json();
          if (data.payment_status === 'paid') {
            setPaymentStatus('success');
            setPolling(false);
            fetchMySub();
            // Refresh user to update subscription_status from pending_payment to active
            if (refreshUser) refreshUser();
            return;
          } else if (data.status === 'expired') {
            setPaymentStatus('expired');
            setPolling(false);
            return;
          }
        }
      } catch (err) {
        console.error(err);
      }
      attempts++;
      if (attempts < maxAttempts) {
        setTimeout(poll, 2000);
      } else {
        setPaymentStatus('timeout');
        setPolling(false);
      }
    };
    poll();
  }, [sessionId, paymentStatus, fetchMySub]);

  const validateDiscount = async (tierKey) => {
    const code = (discountInput[tierKey] || '').trim();
    if (!code) return;
    setValidatingCode(tierKey);
    try {
      const res = await fetch(`${API_URL}/api/discount-codes/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      const data = await res.json();
      setDiscountValid(prev => ({ ...prev, [tierKey]: data.valid }));
      setDiscountInfo(prev => ({ ...prev, [tierKey]: data.valid ? data : null }));
      if (data.valid) {
        toast.success(`${data.discount_percent}% discount — will be applied at checkout`);
      } else {
        toast.error(data.message || 'Invalid discount code');
      }
    } catch {
      toast.error('Failed to validate code');
      setDiscountValid(prev => ({ ...prev, [tierKey]: false }));
    }
    setValidatingCode(null);
  };

  const handleCheckout = async (tier, interval) => {
    if (!isAuthenticated()) {
      toast.error('Please sign in first');
      return;
    }
    if (!renewalConsent[tier]) {
      toast.error('Please acknowledge the auto-renewal terms before subscribing');
      return;
    }
    
    const planId = `${tier}_${interval}`;
    setCheckingOut(planId);
    
    try {
      const res = await fetch(`${API_URL}/api/membership/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          plan_id: planId,
          origin_url: window.location.origin,
          discount_code: discountValid[tier] ? discountInput[tier] : null,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        window.location.href = data.url;
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Checkout failed');
      }
    } catch {
      toast.error('Network error');
    }
    setCheckingOut(null);
  };

  const selectInterval = (tier, interval) => {
    setSelectedInterval(prev => ({ ...prev, [tier]: interval }));
  };

  const getSelectedInterval = (tier) => {
    return selectedInterval[tier] || 'quarterly';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-rooam-black flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-cyan-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-rooam-black" data-testid="membership-page">
      <div className="max-w-6xl mx-auto px-4 py-8 sm:py-12">
        <button onClick={() => window.history.back()} className="inline-flex items-center gap-1 text-gray-400 hover:text-white text-sm mb-8">
          <ChevronLeft className="h-4 w-4" /> Back
        </button>

        {/* New Signup Banner */}
        {isNewSignup && !sessionId && (
          <div className="mb-8 p-5 rounded-xl border bg-rooam-blue/10 border-rooam-blue/30 text-center" data-testid="signup-payment-banner">
            <h2 className="text-white font-bold text-lg mb-1">Almost There!</h2>
            <p className="text-zinc-300 text-sm">Your account has been created. Choose a membership plan below to unlock full access to Rooam Sports.</p>
          </div>
        )}

        {/* Payment Status Banner */}
        {sessionId && (
          <div
            className={`mb-8 p-4 rounded-xl border text-center ${
              paymentStatus === 'success'
                ? 'bg-green-500/10 border-green-500/30'
                : paymentStatus === 'expired' || paymentStatus === 'timeout'
                ? 'bg-red-500/10 border-red-500/30'
                : 'bg-cyan-500/10 border-cyan-500/30'
            }`}
            data-testid="payment-status-banner"
          >
            {polling && (
              <div className="flex items-center justify-center gap-3">
                <Loader2 className="h-5 w-5 text-cyan-500 animate-spin" />
                <p className="text-white">Verifying your payment...</p>
              </div>
            )}
            {paymentStatus === 'success' && (
              <div className="flex items-center justify-center gap-3">
                <CheckCircle className="h-6 w-6 text-green-400" />
                <div>
                  <p className="text-white font-semibold">Payment successful!</p>
                  <p className="text-green-400 text-sm">Your membership is now active. Enjoy full access.</p>
                </div>
              </div>
            )}
            {(paymentStatus === 'expired' || paymentStatus === 'timeout') && (
              <div className="flex items-center justify-center gap-3">
                <XCircle className="h-6 w-6 text-red-400" />
                <div>
                  <p className="text-white font-semibold">Payment could not be verified</p>
                  <p className="text-red-400 text-sm">Please try again or contact support@rooamsports.network.</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-3" data-testid="membership-title">
            Choose Your Plan
          </h1>
          <p className="text-zinc-400 text-sm sm:text-base max-w-xl mx-auto">
            Plans for every level — fans, teams, and recruiting coaches.
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-10" data-testid="membership-tabs">
          {CATEGORY_TABS.map((tab) => {
            const TabIcon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all ${
                  isActive
                    ? 'bg-cyan-500 text-white'
                    : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-white'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <TabIcon className="h-4 w-4" />
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
              </button>
            );
          })}
        </div>

        {/* Tab Description */}
        <div className="text-center mb-8">
          <p className="text-zinc-500 text-sm">
            {CATEGORY_TABS.find((t) => t.id === activeTab)?.description}
          </p>
        </div>

        {/* Current subscription */}
        {mySub && mySub.plan_id !== 'free' && (
          <div className="mb-8 p-4 bg-cyan-500/5 border border-cyan-500/20 rounded-xl text-center max-w-md mx-auto">
            <p className="text-sm text-gray-400">Your current plan</p>
            <p className="text-white font-bold text-lg">{mySub.plan_name}</p>
            <Badge className="bg-green-500/20 text-green-400 border-green-500 mt-1">Active</Badge>
          </div>
        )}

        {/* Fan Plans with All Pricing */}
        {activeTab === 'fans' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {FAN_TIERS.map((tier) => {
              const Icon = TIER_ICONS[tier.tier] || Shield;
              const selected = getSelectedInterval(tier.tier);
              const isCurrentPlan = mySub?.tier === tier.tier;

              return (
                <Card
                  key={tier.tier}
                  className={`relative overflow-hidden transition-all hover:scale-[1.02] ${
                    tier.highlight ? 'ring-2 bg-zinc-900 border-transparent' : 'bg-zinc-900 border-zinc-800'
                  }`}
                  style={tier.highlight ? { '--tw-ring-color': tier.color } : {}}
                  data-testid={`plan-card-${tier.tier}`}
                >
                  {tier.badge && (
                    <div
                      className="absolute top-0 left-0 right-0 text-center text-xs font-bold py-1 text-white"
                      style={{ backgroundColor: tier.color }}
                    >
                      {tier.badge}
                    </div>
                  )}
                  <CardContent className={`p-5 ${tier.badge ? 'pt-8' : 'pt-5'}`}>
                    {/* Plan icon & name */}
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className="h-5 w-5" style={{ color: tier.color }} />
                      <h3 className="text-white font-bold text-base">{tier.name}</h3>
                    </div>
                    <p className="text-zinc-500 text-xs mb-4">{tier.tagline}</p>

                    {/* Pricing Display */}
                    <div className="space-y-2 mb-5 p-3 bg-zinc-950 rounded-lg">
                      {/* Purchasable options: quarterly and yearly only */}
                      {['quarterly', 'yearly'].map((interval) => (
                        <button
                          key={interval}
                          onClick={() => selectInterval(tier.tier, interval)}
                          className={`w-full text-left px-3 py-2 rounded-md transition-all flex items-center justify-between ${
                            selected === interval
                              ? 'bg-zinc-800 border border-zinc-600'
                              : 'hover:bg-zinc-800/50'
                          }`}
                          data-testid={`interval-${tier.tier}-${interval}`}
                        >
                          <span className={`text-sm ${selected === interval ? 'text-white font-medium' : 'text-zinc-400'}`}>
                            {tier.pricing[interval].label}
                          </span>
                          {selected === interval && (
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: tier.color }} />
                          )}
                        </button>
                      ))}
                    </div>

                    {/* Features */}
                    <ul className="space-y-2 mb-4">
                      {tier.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          {feature.endsWith(':') ? (
                            <span className="text-zinc-300 font-medium text-xs mt-2 first:mt-0">{feature}</span>
                          ) : (
                            <>
                              <Check className="h-3.5 w-3.5 shrink-0 mt-0.5" style={{ color: tier.color }} />
                              <span className="text-zinc-400 text-xs leading-relaxed">{feature}</span>
                            </>
                          )}
                        </li>
                      ))}
                    </ul>

                    {/* Not Included */}
                    {tier.notIncluded && tier.notIncluded.length > 0 && (
                      <ul className="space-y-1 mb-4 pt-2 border-t border-zinc-800">
                        {tier.notIncluded.map((item, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <X className="h-3.5 w-3.5 shrink-0 mt-0.5 text-zinc-600" />
                            <span className="text-zinc-600 text-xs">{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* Perfect for */}
                    <p className="text-[11px] text-zinc-600 italic mb-4">Built for: {tier.perfectFor}</p>

                    {/* Auto-Renewal Disclosure - California ARL Compliance */}
                    {!isCurrentPlan && (
                      <div className="mb-3 p-2.5 bg-zinc-950 border border-zinc-700 rounded-lg" data-testid={`renewal-disclosure-${tier.tier}`}>
                        <p className="text-[10px] text-zinc-400 leading-relaxed mb-2">
                          Your subscription will <b className="text-zinc-300">automatically renew</b> at the end of each {selected === 'quarterly' ? 'quarter' : 'year'} at the then-current rate (<b className="text-zinc-300">{tier.pricing[selected].label}</b>) until you cancel. You may cancel at any time from your Account page or by emailing support@rooamsports.network. Cancellation takes effect at the end of your current billing period.
                        </p>
                        <label className="flex items-start gap-2 cursor-pointer" data-testid={`renewal-consent-${tier.tier}`}>
                          <input
                            type="checkbox"
                            checked={!!renewalConsent[tier.tier]}
                            onChange={(e) => setRenewalConsent(prev => ({ ...prev, [tier.tier]: e.target.checked }))}
                            className="mt-0.5 w-3.5 h-3.5 rounded border-zinc-600 bg-zinc-800 accent-cyan-500"
                            data-testid={`renewal-checkbox-${tier.tier}`}
                          />
                          <span className="text-[10px] text-zinc-400 leading-relaxed">
                            I agree to the <Link to="/legal" className="text-cyan-400 hover:underline">auto-renewal terms</Link> and understand I will be charged automatically until I cancel.
                          </span>
                        </label>
                      </div>
                    )}

                    {/* Discount Code - only visible at checkout stage */}
                    {isAuthenticated() && !isCurrentPlan && (
                      <div className="mb-3 flex gap-1.5" data-testid={`discount-section-${tier.tier}`}>
                        <input
                          type="text"
                          value={discountInput[tier.tier] || ''}
                          onChange={(e) => { setDiscountInput(prev => ({ ...prev, [tier.tier]: e.target.value.toUpperCase() })); setDiscountValid(prev => ({ ...prev, [tier.tier]: null })); }}
                          placeholder="Discount code"
                          className={`flex-1 px-2 py-1.5 bg-zinc-950 border rounded text-white text-xs placeholder-zinc-600 focus:outline-none focus:ring-1 ${
                            discountValid[tier.tier] === true ? 'border-green-500 focus:ring-green-500' :
                            discountValid[tier.tier] === false ? 'border-red-500 focus:ring-red-500' :
                            'border-zinc-700 focus:ring-cyan-500'
                          }`}
                          data-testid={`discount-input-${tier.tier}`}
                        />
                        <Button
                          size="sm"
                          onClick={() => validateDiscount(tier.tier)}
                          disabled={!(discountInput[tier.tier] || '').trim() || validatingCode === tier.tier}
                          className="bg-zinc-700 hover:bg-zinc-600 text-white text-xs px-3 h-8"
                          data-testid={`discount-apply-${tier.tier}`}
                        >
                          {validatingCode === tier.tier ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Apply'}
                        </Button>
                      </div>
                    )}
                    {discountValid[tier.tier] === true && (
                      <p className="text-[10px] text-green-400 mb-2 -mt-1" data-testid={`discount-applied-${tier.tier}`}>
                        {discountInfo[tier.tier]?.discount_percent}% off at checkout
                      </p>
                    )}

                    {/* CTA Button */}
                    {isCurrentPlan ? (
                      <Button disabled className="w-full bg-green-500/20 text-green-400 border border-green-500">
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Current Plan
                      </Button>
                    ) : (
                      <Button
                        className="w-full font-semibold text-white transition-all"
                        style={{
                          backgroundColor: tier.color,
                          opacity: checkingOut ? 0.7 : 1,
                        }}
                        onClick={() => (isAuthenticated() ? handleCheckout(tier.tier, selected) : null)}
                        disabled={!!checkingOut}
                        data-testid={`checkout-btn-${tier.tier}`}
                        asChild={!isAuthenticated()}
                      >
                        {checkingOut === `${tier.tier}_${selected}` ? (
                          <span className="flex items-center justify-center">
                            <Loader2 className="h-4 w-4 animate-spin" />
                          </span>
                        ) : isAuthenticated() ? (
                          <span>Subscribe Now</span>
                        ) : (
                          <Link to="/register" className="flex items-center justify-center">
                            Sign Up <ArrowRight className="h-4 w-4 ml-2" />
                          </Link>
                        )}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* FAQ - only show for fans tab */}
        {activeTab === 'fans' && (
          <div className="mt-16 max-w-2xl mx-auto">
            <h2 className="text-white font-bold text-xl text-center mb-6">Common Questions</h2>
            <div className="space-y-3">
              {[
                {
                  q: 'What\'s the difference between plans?',
                  a: 'Basic is for watching only. Plus adds highlight clipping and athlete profiles. Elite includes AI highlights, advanced stats, and recruiting exposure.',
                },
                {
                  q: 'Can I cancel anytime?',
                  a: 'Yes. Cancel your subscription at any time from your Account page or by emailing support@rooamsports.network. Your access continues until the end of your current billing period, and you will not be charged again.',
                },
                {
                  q: 'Can I upgrade my plan?',
                  a: 'Absolutely. Upgrade at any time and only pay the difference for the remainder of your billing period.',
                },
                {
                  q: 'Do subscriptions auto-renew?',
                  a: 'Yes. All subscriptions automatically renew at the end of each billing period (quarterly or annually) at the current rate. You must cancel before your renewal date to avoid being charged. You can cancel anytime from your Account page.',
                },
                {
                  q: 'What payment methods do you accept?',
                  a: 'We accept all major credit and debit cards through our secure Stripe checkout.',
                },
                {
                  q: 'Do I need a membership to browse?',
                  a: 'No. You can browse events, teams, and athlete profiles for free. A membership unlocks full game streaming and replays.',
                },
              ].map((faq, i) => (
                <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
                  <h3 className="text-white font-medium text-sm mb-1">{faq.q}</h3>
                  <p className="text-zinc-500 text-sm">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="h-16" />
    </div>
  );
};

export default Membership;
